import { Button, GlobeIcon, MailIcon, Skeleton, Typography } from '@/components/atoms';
import { TextHeader } from '@/components/molecules/header/TextHeader';
import { useTranslation, useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import { Image } from 'expo-image';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Dimensions, Linking, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

interface VentureScreenProps {
    ventureId?: string | undefined;
    ventureName?: string | null | undefined;
    ventureLogo?: string | null | undefined;
    ventureDescription?: string | null | undefined;
    ventureImage?: string | null | undefined;
    servicesHeading?: string | null | undefined;
    services?: {
        id: string;
        name: string;
        imageUrl: string | null;
        link?: string | null | undefined;
    }[] | undefined;
    websiteUrl?: string | null | undefined;
    contactEmail?: string | null | undefined;
    isLoading?: boolean | undefined;
}

export default function VentureScreen({
    ventureName,
    ventureLogo,
    ventureDescription,
    ventureImage,
    servicesHeading,
    services = [],
    websiteUrl,
    contactEmail,
    isLoading = false,
}: VentureScreenProps) {
    const colors = useUIColors();
    const { t } = useTranslation();

    const handleBackPress = () => {
        router.back();
    };

    const handleWebsitePress = () => {
        if (websiteUrl) {
            Linking.openURL(websiteUrl).catch((error) => logger.error('VentureScreen', 'Website open failed', error));
        } else {
            logger.warn('VentureScreen', 'Website URL missing');
        }
    };

    const handleContactPress = () => {
        if (contactEmail) {
            Linking.openURL(`mailto:${contactEmail}`).catch((error) => logger.error('VentureScreen', 'Contact open failed', error));
        } else {
            logger.warn('VentureScreen', 'Contact email missing');
        }
    };

    const handleServicePress = (link?: string | null, serviceId?: string) => {
        if (link) {
            Linking.openURL(link).catch((error) => logger.error('VentureScreen', 'Service link open failed', error));
            return;
        }
        logger.info('VentureScreen', 'Service pressed:', serviceId);
    };

    const [logoLoading, setLogoLoading] = useState<boolean>(!!ventureLogo);
    const [heroLoading, setHeroLoading] = useState<boolean>(!!ventureImage);

    const showLogoSkeleton = isLoading || logoLoading;
    const showHeroSkeleton = isLoading || heroLoading;

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
            <TextHeader title="" onBackPress={handleBackPress} />

            <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
                {/* Venture Logo - Centered at top */}
                <View style={styles.logoContainer}>
                    {ventureLogo ? (
                        <>
                            <Image
                                source={{ uri: ventureLogo }}
                                style={styles.logo}
                                contentFit="contain"
                                cachePolicy="memory-disk"
                                transition={200}
                                onLoadStart={() => setLogoLoading(true)}
                                onLoadEnd={() => setLogoLoading(false)}
                                onLoad={() => setLogoLoading(false)}
                                onError={() => setLogoLoading(false)}
                            />
                            {showLogoSkeleton && <Skeleton width={140} height={64} borderRadius={0} style={styles.logoSkeleton} />}
                        </>
                    ) : (
                        showLogoSkeleton && <Skeleton width={140} height={64} borderRadius={0} />
                    )}
                </View>

                {/* Two-column layout: Image on left, Content on right */}
                <View style={styles.contentLayout}>
                    {/* Left Column - Venture Image */}
                    <View style={styles.imageColumn}>
                    {ventureImage ? (
                        <>
                            <Image
                                source={{ uri: ventureImage }}
                                style={styles.ventureImage}
                                contentFit="cover"
                                cachePolicy="memory-disk"
                                transition={200}
                                onLoadStart={() => setHeroLoading(true)}
                                onLoadEnd={() => setHeroLoading(false)}
                                onLoad={() => setHeroLoading(false)}
                                onError={() => setHeroLoading(false)}
                            />
                                {showHeroSkeleton && <Skeleton width="100%" height="100%" borderRadius={12} style={styles.heroSkeleton} />}
                        </>
                    ) : (
                            showHeroSkeleton && <Skeleton width="100%" height={484} borderRadius={12} />
                    )}
                </View>

                    {/* Right Column - Content */}
                    <View style={styles.contentColumn}>
                {/* Venture Description */}
                <View style={styles.descriptionContainer}>
                    {isLoading ? (
                        <View style={{ gap: 8 }}>
                            <Skeleton width="100%" height={16} />
                            <Skeleton width="92%" height={16} />
                            <Skeleton width="85%" height={16} />
                        </View>
                    ) : (
                        <Typography variant="body" color="tertiary" style={styles.description}>
                            {ventureDescription}
                        </Typography>
                    )}
                </View>

                {/* Action Buttons */}
                {isLoading ? (
                    <View style={styles.buttonsContainer}>
                        <Skeleton width="48%" height={44} borderRadius={8} />
                        <Skeleton width="48%" height={44} borderRadius={8} />
                    </View>
                ) : (
                    <View style={styles.buttonsContainer}>
                        <Button
                            title={t('ventures.website' as any)}
                            variant="outline"
                            onPress={handleWebsitePress}
                            style={[styles.actionButton, { backgroundColor: colors.backgroundLevel1 }]}
                                    leading={<GlobeIcon size={18} />}
                            disabled={!websiteUrl}
                        />
                        <Button
                            title={t('ventures.contact' as any)}
                            variant="outline"
                            onPress={handleContactPress}
                            style={[styles.actionButton, { backgroundColor: colors.backgroundLevel1 }]}
                                    leading={<MailIcon size={18} strokeWidth={2.5} />}
                            disabled={!contactEmail}
                        />
                    </View>
                )}

                {/* Services grid */}
                {isLoading ? (
                    <View style={styles.servicesSection}>
                                <Skeleton width={180} height={16} style={{ marginBottom: 8 }} />
                        <View style={styles.servicesGrid}>
                                    {[...Array(2)].map((_, idx) => (
                                <Skeleton key={idx} width="48%" height={160} borderRadius={12} />
                            ))}
                        </View>
                    </View>
                ) : services.length > 0 ? (
                    <View style={styles.servicesSection}>
                        <Typography variant="headline" color="primary" style={styles.servicesTitle}>
                            {servicesHeading || 'Our Services'}
                        </Typography>
                        <View style={styles.servicesGrid}>
                            {services.map((service) => (
                                <TouchableOpacity
                                    key={service.id}
                                    style={[styles.serviceCard, { backgroundColor: colors.backgroundLevel1 }]}
                                    activeOpacity={0.85}
                                    onPress={() => handleServicePress(service.link, service.id)}
                                >
                                    {service.imageUrl ? (
                                        <Image
                                            source={{ uri: service.imageUrl }}
                                            style={styles.serviceImage}
                                            contentFit="cover"
                                            cachePolicy="memory-disk"
                                            transition={200}
                                        />
                                    ) : (
                                        <Skeleton width="100%" height={120} borderRadius={8} />
                                    )}
                                    <Typography variant="subhead" color="primary" style={styles.serviceName} numberOfLines={2}>
                                        {service.name}
                                    </Typography>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>
                ) : null}
                    </View>
                </View>
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingHorizontal: 16,
        paddingBottom: 100,
    },
    logoContainer: {
        alignItems: 'center',
        marginBottom: 24,
    },
    logo: {
        width: 140,
        height: 64,
    },
    logoSkeleton: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    },
    contentLayout: {
        flexDirection: isWeb && screenWidth > 768 ? 'row' : 'column',
        gap: 24,
    },
    imageColumn: {
        width: isWeb && screenWidth > 768 ? '52%' : '100%',
        minHeight: 484,
    },
    ventureImage: {
        width: '100%',
        height: 484,
        borderRadius: 12,
    },
    heroSkeleton: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    },
    contentColumn: {
        flex: 1,
        gap: 32,
    },
    descriptionContainer: {
        paddingHorizontal: 4,
    },
    description: {
        lineHeight: 22,
        textAlign: 'left',
    },
    buttonsContainer: {
        flexDirection: 'row',
        gap: 12,
        paddingHorizontal: 0,
    },
    actionButton: {
        flex: 1,
        borderWidth: 0,
    },
    servicesSection: {
        gap: 8,
    },
    servicesTitle: {
        marginBottom: 4,
    },
    servicesGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: 10,
        rowGap: 12,
    },
    serviceCard: {
        width: '48.5%',
        borderRadius: 12,
        padding: 6,
        gap: 12,
        borderWidth: 0.5,
        borderColor: '#e5e5e5',
    },
    serviceImage: {
        width: '100%',
        aspectRatio: 120 / 80,
        borderRadius: 8,
    },
    serviceName: {
        paddingHorizontal: 4,
        paddingBottom: 4,
        textAlign: 'left',
    },
});
